import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useRedirectTarget } from "../auth/redirect";
import { Button } from "../components/ui/Button";
import { Turnstile } from "../components/Turnstile";
import { getEnv } from "../config/env";
import { GoogleSignInButton } from "../components/GoogleSignInButton";
import { Logo } from "../components/Logo";
import { ThemeToggle } from "../components/ThemeToggle";

export function Login() {
  const { login, completeMfa, completeEmailMfa } = useAuth();
  const navigate = useNavigate();
  const redirectTo = useRedirectTarget();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mfaFactor, setMfaFactor] = useState<string | null>(null);
  const [emailMfa, setEmailMfa] = useState(false);
  const [code, setCode] = useState("");
  const [captcha, setCaptcha] = useState<string | null>(null);
  const needsCaptcha = Boolean(getEnv().VITE_TURNSTILE_SITE_KEY);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await login(email, password, captcha);
      if (res.mfaRequired && res.factorId) {
        setMfaFactor(res.factorId);
      } else if (res.emailMfaRequired) {
        setEmailMfa(true);
      } else {
        navigate(redirectTo);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign in failed");
    } finally {
      setBusy(false);
    }
  }

  async function onEmailMfaSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await completeEmailMfa(code);
      navigate(redirectTo);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid code");
    } finally {
      setBusy(false);
    }
  }

  async function onMfaSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await completeMfa(mfaFactor!, code);
      navigate(redirectTo);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid code");
    } finally {
      setBusy(false);
    }
  }

  if (emailMfa) {
    return (
      <div className="mx-auto grid min-h-full max-w-sm content-center gap-5 px-6 py-16">
        <h1 className="font-display text-2xl font-black tracking-tight">Check your email</h1>
        <p className="text-sm text-muted">We sent a 6-digit code to your email. Enter it to finish signing in.</p>
        <form className="grid gap-3" onSubmit={onEmailMfaSubmit}>
          <input
            className="rounded-lg border border-line bg-surface-2 px-3 py-2 text-center font-mono text-lg tracking-widest text-ink"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            required
          />
          {error && <p className="font-mono text-sm text-danger">{error}</p>}
          <Button type="submit" disabled={busy || code.length < 6}>
            {busy ? "Verifying…" : "Verify"}
          </Button>
        </form>
      </div>
    );
  }

  if (mfaFactor) {
    return (
      <div className="mx-auto grid min-h-full max-w-sm content-center gap-5 px-6 py-16">
        <h1 className="font-display text-2xl font-black tracking-tight">Two-factor code</h1>
        <p className="text-sm text-muted">Enter the 6-digit code from your authenticator app.</p>
        <form className="grid gap-3" onSubmit={onMfaSubmit}>
          <input
            className="rounded-lg border border-line bg-surface-2 px-3 py-2 text-center font-mono text-lg tracking-widest text-ink"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            required
          />
          {error && <p className="font-mono text-sm text-danger">{error}</p>}
          <Button type="submit" disabled={busy || code.length < 6}>
            {busy ? "Verifying…" : "Verify"}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="mx-auto grid min-h-full max-w-sm content-center gap-5 px-6 py-16">
      <div className="fixed right-4 top-4 z-10">
        <ThemeToggle />
      </div>
      <Logo size={44} className="mx-auto" />
      <h1 className="text-center font-display text-2xl font-black tracking-tight">Sign in</h1>
      <form className="grid gap-3" onSubmit={onSubmit}>
        <label className="grid gap-1 text-xs font-bold text-muted">
          Email
          <input
            className="rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-ink"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>
        <label className="grid gap-1 text-xs font-bold text-muted">
          Password
          <input
            className="rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-ink"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        <Turnstile onToken={setCaptcha} />
        {error && <p className="font-mono text-sm text-danger">{error}</p>}
        <Button type="submit" disabled={busy || (needsCaptcha && !captcha)}>
          {busy ? "Signing in…" : "Sign in"}
        </Button>
      </form>
      <div className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-widest text-muted">
        <span className="h-px flex-1 bg-line" />
        or
        <span className="h-px flex-1 bg-line" />
      </div>
      <GoogleSignInButton label="Continue with Google" redirectTo={redirectTo} />
      <p className="text-sm text-muted">
        New here?{" "}
        <Link className="font-bold text-accent-strong" to={`/register?redirect=${encodeURIComponent(redirectTo)}`}>
          Create an account
        </Link>
      </p>
    </div>
  );
}
